import { styled } from '@linaria/react';
import { Group } from '@mantine/core';
import type { TFunction } from 'i18next';
import {
  AgentSessionLabel,
  type AgentMinerSessionFacets,
} from '../../../../api/agent-miner-client/agent-miner-client.type';
import { AgentSource } from '../../../../api/events-client/events-client.type';
import type { FilterGroup } from '../../../../components/filters/filter-panel.type';
import { AGENT_SOURCES_ICONS, AGENT_SOURCES_LABELS } from '../../../agentic-process/agentic-process.type';
import { formatAnnotationLabel } from '../../shared/shared.helpers';
import type { SessionsFiltersValue } from './session-filters.type';

const SourceIcon = styled.img`
  object-fit: contain;
`;

type GroupDeps = {
  facets: AgentMinerSessionFacets | undefined;
  value: SessionsFiltersValue;
  setSources: (values: AgentSource[]) => void;
  setUserIds: (values: string[]) => void;
  setLabels: (values: AgentSessionLabel[]) => void;
  setHasTask: (value: boolean | null) => void;
  setHideEmpty: (value: boolean) => void;
  setModelFamilies: (values: string[]) => void;
  t: TFunction;
};

const HAS_TASK_ANY = 'any';
const HAS_TASK_TRUE = 'linked';
const HAS_TASK_FALSE = 'unlinked';

/**
 * Returns an array of session filter groups for the agent miner sessions list.
 *
 * @param {Object} params - The dependencies required to generate filter groups.
 * @param {AgentMinerSessionFacets | undefined} params.facets - The session facets for available filter options.
 * @param {SessionsFiltersValue} params.value - The current filter values.
 * @param {(values: AgentSource[]) => void} params.setSources - Callback to update selected source filters.
 * @param {(values: string[]) => void} params.setUserIds - Callback to update selected user ID filters.
 * @param {(value: boolean | null) => void} params.setHasTask - Callback to update the linked task filter.
 * @param {(value: boolean) => void} params.setHideEmpty - Callback to update the hide empty filter.
 * @param {(values: string[]) => void} params.setModelFamilies - Callback to update the model family filter.
 * @returns {FilterGroup[]} The array of filter group objects to be displayed in the filter panel.
 */

const getSessionsFilterGroups = ({
  facets,
  value,
  setSources,
  setUserIds,
  setLabels,
  setHasTask,
  setHideEmpty,
  setModelFamilies,
  t,
}: GroupDeps): FilterGroup[] => {
  const sourceOptions = (facets?.sources ?? []).map((source) => ({
    value: source,
    label: AGENT_SOURCES_LABELS[source] ?? source,
  }));

  const userOptions = (facets?.users ?? []).map((user) => ({
    value: user.id,
    label: user.name || user.id,
  }));

  const labelOptions = Object.values(AgentSessionLabel).map((label) => ({
    value: label,
    label: formatAnnotationLabel(label) ?? label,
  }));

  // Union the facet list with whatever is currently selected: a family can
  // arrive by deep link (the Model Comparison tab's "expand" action) before
  // the facets query resolves, and an option-less selection would render as an
  // invisible, unremovable filter.
  const modelFamilyOptions = Array.from(new Set([...(facets?.model_families ?? []), ...value.modelFamilies]))
    .sort((a, b) => a.localeCompare(b))
    .map((family) => ({ value: family, label: family }));

  const linkedTaskOptions = [
    { value: HAS_TASK_ANY, label: 'Any' },
    { value: HAS_TASK_TRUE, label: `Has linked ${t('common.work_unit')}` },
    { value: HAS_TASK_FALSE, label: `No linked ${t('common.work_unit')}` },
  ];

  const linkedTaskSelection =
    value.hasTask == null ? [HAS_TASK_ANY] : value.hasTask ? [HAS_TASK_TRUE] : [HAS_TASK_FALSE];

  const modelFamilyGroups: FilterGroup[] =
    modelFamilyOptions.length > 0
      ? [
          {
            name: t('agent_miner.model_comparison.model'),
            icon: 'smart_toy',
            options: modelFamilyOptions,
            selectedValues: value.modelFamilies,
            onSelectionChange: (values) => setModelFamilies(values),
            optional: true,
            multiSelect: true,
            searchable: true,
          },
        ]
      : [];

  return [
    {
      name: t('agent_miner.source'),
      icon: 'filter_list',
      options: sourceOptions,
      selectedValues: value.sources,
      onSelectionChange: (values) => setSources(values as AgentSource[]),
      optional: true,
      multiSelect: true,
      searchable: true,
      renderOption: ({ option }) => {
        const iconSrc = AGENT_SOURCES_ICONS[option.value as AgentSource];
        const label = AGENT_SOURCES_LABELS[option.value as AgentSource] ?? option.label;
        return (
          <Group gap={8} wrap="nowrap">
            {iconSrc && <SourceIcon src={iconSrc} alt="" width={16} height={16} />}
            <span>{label}</span>
          </Group>
        );
      },
    },
    {
      name: 'User',
      icon: 'person',
      options: userOptions,
      selectedValues: value.userIds,
      onSelectionChange: (values) => setUserIds(values),
      optional: true,
      multiSelect: true,
      searchable: true,
    },
    ...modelFamilyGroups,
    {
      name: t('agent_miner.category'),
      icon: 'sell',
      options: labelOptions,
      selectedValues: value.labels,
      onSelectionChange: (values) => setLabels(values as AgentSessionLabel[]),
      optional: true,
      multiSelect: true,
    },
    {
      name: `Linked ${t('common.work_unit')}`,
      icon: 'link',
      options: linkedTaskOptions,
      selectedValues: linkedTaskSelection,
      onSelectionChange: (values) => {
        const chosen = values[0];
        if (chosen === HAS_TASK_TRUE) setHasTask(true);
        else if (chosen === HAS_TASK_FALSE) setHasTask(false);
        else setHasTask(null);
      },
      // Radio-style: exactly one option is always selected (defaults to "Any").
      // `showAll: false` disables FilterPanel's auto "All" checkbox, which
      // doesn't round-trip cleanly when the underlying filter is tri-state.
      showAll: false,
    },
    {
      name: 'Activity',
      icon: 'visibility_off',
      // Single-option toggle group — clicking the checkbox flips the boolean.
      // `showAll: false` makes FilterPanel treat this as a toggle (clicking
      // with one option swaps between `[]` and `[value]`).
      options: [{ value: 'hide_empty', label: `Hide empty ${t('agent_miner.sessions').toLowerCase()}` }],
      selectedValues: value.hideEmpty ? ['hide_empty'] : [],
      onSelectionChange: (values) => setHideEmpty(values.length > 0),
      showAll: false,
    },
  ];
};

export { getSessionsFilterGroups };
